/**
 * ActivityCompletionWizard — unified modal shell for completing care plan activities.
 * Renders task-type-specific completion content from a registry and falls back to
 * default completion content for unregistered activity/task codes.
 * @agentic connections
 */

import { Alert, Stack } from "@mantine/core";
import type { ActivityCompletionData } from "@benchmark/core/constants";
import type {
  Patient,
  Practitioner,
  PractitionerRole,
  Task,
} from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ComponentType,
  ForwardRefExoticComponent,
  RefAttributes,
} from "react";
import { Modal } from "@benchmark/ui/components/atomic";
import { handleActivityCompletion } from "@benchmark/core/workflows";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { usePatientCoverage } from "@/hooks/domain/usePatientCoverage";
import { getPatientDisplayName } from "@benchmark/core/resolvers";
import { showSuccessNotification } from "@benchmark/ui/utils";
import { useConfirmation } from "@benchmark/ui/hooks";
import { useBeforeUnload } from "@benchmark/ui/hooks";
import { ConfirmationPresets } from "@benchmark/ui/utils";
import { useActivityProgressLogs } from "@/hooks/domain/useActivityProgressLogs";
import { useNextActivityOwner } from "@/hooks/domain/useNextActivityOwner";
import { findBenchmarkPractitionerRole } from "@benchmark/core/services";
import { ConfirmServicesCompletionContent } from "./completion-content/ConfirmServicesCompletionContent";
import { ConsultCompletionContent } from "./completion-content/ConsultCompletionContent";
import { DefaultCompletionContent } from "./completion-content/DefaultCompletionContent";
import { EncounterCompletionContent } from "./completion-content/EncounterCompletionContent";
import { BuildCoCMCarePlanCompletionContent } from "./completion-content/BuildCoCMCarePlanCompletionContent";
import { FollowUpOnServicesCompletionContent } from "./completion-content/FollowUpOnServicesCompletionContent";
import { ScheduleServicesCompletionContent } from "./completion-content/ScheduleServicesCompletionContent";
import { IntakeCompletionContent } from "./completion-content/IntakeCompletionContent";
import { OfferServicesCompletionContent } from "./completion-content/OfferServicesCompletionContent";
import { FinalizeCarePlanEnrollmentCompletionContent } from "./completion-content/FinalizeCarePlanEnrollmentCompletionContent";
import { ProgressLogHistory } from "./progress-content/ProgressLogHistory";
import { ApproveSelectedServicesCompletionContent } from "./completion-content/ApproveSelectedServicesCompletionContent";
import { ScheduleIntakeCompletionContent } from "./completion-content/ScheduleIntakeCompletionContent";
import { SelectServicesCompletionContent } from "./completion-content/SelectServicesCompletionContent";
import { TaskModalHeader } from "./TaskModalHeader";
import type { CompletionContentProps } from "./completion-content/DefaultCompletionContent";
import { ENCOUNTER_DOCUMENTATION_MODAL_PROPS } from "@/constants/encounterDocumentationModal";
import styles from "./ActivityCompletionWizard.module.css";
import { CONFIG } from "@/app/config";

/**
 * Ref handle for self-managed completion content to participate in close protection.
 * @agentic connections
 */
type CompletionContentRef = { checkProtection: () => Promise<boolean> };

/**
 * Completion content component type, supporting both normal and ref-forwarding variants.
 * @agentic connections
 */
type CompletionContentComponent =
  | ComponentType<CompletionContentProps>
  | ForwardRefExoticComponent<
      CompletionContentProps & RefAttributes<CompletionContentRef>
    >;

/**
 * Registry entry metadata for a completion content component.
 * @agentic connections
 */
type CompletionContentRegistryEntry = {
  component: CompletionContentComponent;
  size: "xl" | "near-full-page";
  selfManaged?: boolean;
};

/**
 * Normalizes a Task.owner reference to PractitionerRole id when possible.
 * @agentic connections
 */
const getTaskOwnerPractitionerRoleId = (task: Task): string | undefined => {
  const ownerRef = task.owner?.reference;
  if (!ownerRef) {
    return undefined;
  }

  if (ownerRef.startsWith("PractitionerRole/")) {
    return ownerRef.replace("PractitionerRole/", "");
  }

  return undefined;
};

/**
 * Registry mapping task type codes to completion content configuration.
 * @agentic connections
 */
const COMPLETION_CONTENT_REGISTRY: Record<
  string,
  CompletionContentRegistryEntry
> = {
  "schedule-intake": { component: ScheduleIntakeCompletionContent, size: "xl" },
  "finalize-care-plan-enrollment": {
    component: FinalizeCarePlanEnrollmentCompletionContent,
    size: "xl",
  },
  "select-services": {
    component: SelectServicesCompletionContent,
    size: "xl",
  },
  "offer-services": { component: OfferServicesCompletionContent, size: "xl" },
  "confirm-services": {
    component: ConfirmServicesCompletionContent,
    size: "xl",
  },
  "schedule-services": {
    component: ScheduleServicesCompletionContent,
    size: "near-full-page",
    selfManaged: true,
  },
  "follow-up-on-services": {
    component: FollowUpOnServicesCompletionContent,
    size: "xl",
  },
  "complete-intake": {
    component: IntakeCompletionContent,
    size: "near-full-page",
    selfManaged: true,
  },
  "check-in-on-member": {
    component: EncounterCompletionContent,
    size: "near-full-page",
    selfManaged: true,
  },
  "complete-cadenced-followup": {
    component: EncounterCompletionContent,
    size: "near-full-page",
    selfManaged: true,
  },
  "follow-up-on-cocm": {
    component: EncounterCompletionContent,
    size: "near-full-page",
    selfManaged: true,
  },
  "approve-selected-services": {
    component: ApproveSelectedServicesCompletionContent,
    size: "xl",
  },
  // Note: `enroll-in-mirah` was retired with the Benchmark/Mirah partnership
  // wind-down. Any straggler tasks fall through to `DefaultCompletionContent`,
  // which marks the task complete without writing Mirah identifiers. The
  // supported retirement path for in-flight tasks is the
  // `task skip-care-plan-activity --code enroll-in-mirah` CLI.
  "build-cocm-care-plan": {
    component: BuildCoCMCarePlanCompletionContent,
    size: "xl",
    selfManaged: true,
  },
  "consult-psych": { component: ConsultCompletionContent, size: "xl" },
  "consult-pcp": { component: ConsultCompletionContent, size: "xl" },
  "discharge-member": {
    component: EncounterCompletionContent,
    size: "near-full-page",
    selfManaged: true,
  },
};

/**
 * Props for ActivityCompletionWizard.
 * @agentic connections
 */
export type ActivityCompletionWizardProps = {
  opened: boolean;
  onClose: () => void;
  task: Task | null;
  onComplete: () => void;
};

/**
 * Unified activity completion wizard component.
 * @agentic connections
 */
export const ActivityCompletionWizard = ({
  opened,
  onClose,
  task,
  onComplete,
}: ActivityCompletionWizardProps) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [isDirty, setIsDirty] = useState(false);
  const [practitionerRole, setPractitionerRole] = useState<
    PractitionerRole | undefined
  >(undefined);
  const [ownerOverride, setOwnerOverride] = useState<{
    ref: string;
    display: string;
  } | null>(null);
  const { confirm, ConfirmationModal } = useConfirmation();
  const patientId = task?.for?.reference
    ? getResourceIdFromReference(task.for.reference, "Patient")
    : undefined;
  const { data: patientResourceRaw } = usePatientResource(patientId);
  const patientResource = patientResourceRaw as Patient | undefined;
  const { primary: primaryCoverage } = usePatientCoverage(
    patientResource ?? ({ resourceType: "Patient" } as Patient),
  );
  const { logs: progressLogs, count: logCount } = useActivityProgressLogs(
    opened ? task?.id : undefined,
  );
  const {
    nextActivityLabel,
    defaultOwner,
    appliedRule,
    isResolving: isResolvingNextOwner,
  } = useNextActivityOwner(task, opened);

  useEffect(() => {
    if (!opened || !profile?.id) {
      return;
    }

    findBenchmarkPractitionerRole(medplum, {
      reference: `Practitioner/${profile.id}`,
      benchmarkOrganizationId: CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
    })
      .then(setPractitionerRole)
      .catch(() => setPractitionerRole(undefined));
  }, [medplum, opened, profile?.id]);

  const handleDefaultComplete = useCallback(
    async (statusReason?: string, completionData?: ActivityCompletionData) => {
      if (!task) return;

      const mergedData: ActivityCompletionData = {
        ...completionData,
        ...(ownerOverride &&
          !completionData?.rejectionAction && {
            nextActivityOwnerRef: ownerOverride.ref,
            nextActivityOwnerDisplay: ownerOverride.display,
          }),
      };

      const carePlanRef = task.basedOn?.find((ref) =>
        ref.reference?.startsWith("CarePlan/"),
      );

      if (carePlanRef?.reference) {
        const carePlanId = getResourceIdFromReference(
          carePlanRef.reference,
          "CarePlan",
        );
        if (carePlanId) {
          const carePlan = await medplum.readResource("CarePlan", carePlanId);
          const activityCode = task.code?.coding?.[0]?.code;

          if (activityCode) {
            // Notifications for an activity becoming ready (e.g. service
            // approval) are dispatched centrally from `updateCarePlanActivity`
            // (keyed on the now-ready activity), not from this wizard.
            await handleActivityCompletion(medplum, {
              carePlan,
              activityCode,
              taskId: task.id,
              statusReason,
              completionData: mergedData,
              practitionerRole,
            });
            showSuccessNotification(
              "Activity completed and next step advanced",
            );
            setOwnerOverride(null);
            onComplete();
            onClose();
            return;
          }
        }
      }

      await medplum.updateResource({
        ...task,
        status: "completed",
        ...(statusReason && { statusReason: { text: statusReason } }),
        lastModified: new Date().toISOString(),
        executionPeriod: {
          ...task.executionPeriod,
          end: new Date().toISOString(),
        },
      });
      showSuccessNotification("Task marked as completed");
      setOwnerOverride(null);
      onComplete();
      onClose();
    },
    [medplum, onClose, onComplete, ownerOverride, practitionerRole, task],
  );
  const taskTypeCode = task?.code?.coding?.[0]?.code;
  const registryEntry = taskTypeCode
    ? COMPLETION_CONTENT_REGISTRY[taskTypeCode]
    : undefined;
  useBeforeUnload(opened && isDirty && !registryEntry?.selfManaged);
  const contentRef = useRef<CompletionContentRef | null>(null);
  const hasUnsavedChanges = isDirty;

  const handleDone = useCallback(() => {
    setIsDirty(false);
    setOwnerOverride(null);
    onComplete();
    onClose();
  }, [onClose, onComplete]);
  const noopComplete = useCallback(async () => {}, []);

  const handleClose = useCallback(async () => {
    if (registryEntry?.selfManaged && contentRef.current) {
      const canClose = await contentRef.current.checkProtection();
      if (!canClose) return;
      onClose();
      return;
    }

    if (hasUnsavedChanges) {
      const confirmed = await confirm({
        ...ConfirmationPresets.navigate,
        message: "You have unsaved changes. Are you sure you want to close?",
      });
      if (!confirmed) return;
    }
    setIsDirty(false);
    setOwnerOverride(null);
    onClose();
  }, [confirm, hasUnsavedChanges, onClose, registryEntry?.selfManaged]);

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
  }, []);

  if (!task) return null;

  const ContentComponent = registryEntry?.component ?? DefaultCompletionContent;
  const modalLayoutProps =
    registryEntry?.size === "near-full-page"
      ? ENCOUNTER_DOCUMENTATION_MODAL_PROPS
      : { size: registryEntry?.size ?? "xl" };
  const SelfManagedContentComponent =
    ContentComponent as ForwardRefExoticComponent<
      CompletionContentProps & RefAttributes<CompletionContentRef>
    >;

  const patientName = patientResource
    ? getPatientDisplayName(patientResource)
    : undefined;
  const referringPracticeLabel = patientResource?.managingOrganization?.display;
  const ownerRoleId = getTaskOwnerPractitionerRoleId(task);
  const ownerMismatch =
    !!ownerRoleId &&
    !!practitionerRole?.id &&
    ownerRoleId !== practitionerRole.id;
  const assignedOwnerDisplay =
    task.owner?.display || task.owner?.reference || "another practitioner";

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <TaskModalHeader
          actionLabel="Complete"
          task={task}
          patientName={patientName}
          coverageLabel={primaryCoverage.insuranceInfo}
          insuranceMemberId={primaryCoverage.memberNumber}
          referringPracticeLabel={referringPracticeLabel}
        />
      }
      {...modalLayoutProps}
      contentProps={{ pt: "md" }}
    >
      <Stack gap="md" className={styles.wizardBody}>
        {ownerMismatch && (
          <Alert color="orange" variant="light">
            This activity is assigned to {assignedOwnerDisplay}. You can still
            complete it, but please confirm this is intentional.
          </Alert>
        )}
        {logCount > 0 && <ProgressLogHistory logs={progressLogs} />}
        {registryEntry?.selfManaged ? (
          <SelfManagedContentComponent
            ref={contentRef}
            task={task}
            onComplete={noopComplete}
            onDone={handleDone}
            onCancel={handleClose}
            onDirtyChange={handleDirtyChange}
            nextActivityLabel={nextActivityLabel}
            nextActivityDefaultOwner={defaultOwner}
            nextActivityAppliedRule={appliedRule}
            onNextTaskOwnerOverrideChange={setOwnerOverride}
            isResolvingNextTaskOwner={isResolvingNextOwner}
            nextTaskOwnerOverride={ownerOverride}
          />
        ) : (
          <ContentComponent
            task={task}
            onComplete={handleDefaultComplete}
            onDone={handleDone}
            onCancel={handleClose}
            onDirtyChange={handleDirtyChange}
            nextActivityLabel={nextActivityLabel}
            nextActivityDefaultOwner={defaultOwner}
            nextActivityAppliedRule={appliedRule}
            onNextTaskOwnerOverrideChange={setOwnerOverride}
            isResolvingNextTaskOwner={isResolvingNextOwner}
            nextTaskOwnerOverride={ownerOverride}
          />
        )}
      </Stack>
      <ConfirmationModal />
    </Modal>
  );
};
